import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getStoredToken, 
  getUserPermissions, 
  getCurrentUser,
  clearTokens,
} from '../utils/api';

const AuthContext = createContext(null);
const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "true" || value === "1";
const isSessionEndingError = (error) =>
  error?.status === 401 ||
  error?.code === 'ACCOUNT_INACTIVE' ||
  error?.code === 'SESSION_REVOKED' ||
  error?.code === 'TOKEN_INVALID';

const ALERTS_SETTINGS_PATH = '/alerts';
const LEGACY_ALERTS_SETTINGS_PATH = '/settings/alerts';
const ACTIVITY_SETTINGS_PATH = '/activity-settings';

/** Ensure Alerts / Activity Settings appear under Settings for admins (e.g. before backend reload). */
const normalizeAdminPermissions = (perms) => {
  if (!perms || !isTruthyFlag(perms.is_admin)) return perms;

  const allowedRoutes = Array.isArray(perms.allowed_routes) ? [...perms.allowed_routes] : [];
  if (
    !allowedRoutes.includes(ALERTS_SETTINGS_PATH) &&
    !allowedRoutes.includes(LEGACY_ALERTS_SETTINGS_PATH)
  ) {
    allowedRoutes.push(ALERTS_SETTINGS_PATH);
  } else if (
    allowedRoutes.includes(LEGACY_ALERTS_SETTINGS_PATH) &&
    !allowedRoutes.includes(ALERTS_SETTINGS_PATH)
  ) {
    allowedRoutes.push(ALERTS_SETTINGS_PATH);
  }
  if (!allowedRoutes.includes(ACTIVITY_SETTINGS_PATH)) {
    allowedRoutes.push(ACTIVITY_SETTINGS_PATH);
  }

  const menuItems = Array.isArray(perms.menu_items)
    ? perms.menu_items.map((item) => {
        if (item.text !== 'Settings' || !Array.isArray(item.subItems)) return item;
        let subItems = item.subItems.map((sub) =>
          sub.path === LEGACY_ALERTS_SETTINGS_PATH
            ? { ...sub, path: ALERTS_SETTINGS_PATH }
            : sub
        );
        if (!subItems.some((sub) => sub.path === ALERTS_SETTINGS_PATH)) {
          const alertsItem = { text: 'Alert Rules', path: ALERTS_SETTINGS_PATH };
          const bannerIndex = subItems.findIndex((sub) => sub.path === '/banner-settings');
          if (bannerIndex >= 0) {
            subItems = [...subItems];
            subItems.splice(bannerIndex + 1, 0, alertsItem);
          } else {
            subItems = [...subItems, alertsItem];
          }
        }
        if (!subItems.some((sub) => sub.path === ACTIVITY_SETTINGS_PATH)) {
          const activitySettingsItem = { text: 'Activity Settings', path: ACTIVITY_SETTINGS_PATH };
          const notificationIndex = subItems.findIndex((sub) => sub.path === '/notification-settings');
          if (notificationIndex >= 0) {
            subItems = [...subItems];
            subItems.splice(notificationIndex + 1, 0, activitySettingsItem);
          } else {
            subItems = [...subItems, activitySettingsItem];
          }
        }
        return { ...item, subItems };
      })
    : perms.menu_items;

  return { ...perms, allowed_routes: allowedRoutes, menu_items: menuItems };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Clear authentication - defined first as it's used by other functions
  const clearAuth = useCallback(() => {
    clearTokens();
    setUser(null);
    setPermissions(null);
    setIsAuthenticated(false);
  }, []);

  // Validate the stored credential before exposing any cached identity or role.
  const initializeUser = useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token) {
        setUser(null);
        setPermissions(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const userData = await getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      try {
        const userPerms = await getUserPermissions();
        setPermissions(normalizeAdminPermissions(userPerms));
      } catch (permissionsError) {
        console.error('Failed to fetch user permissions:', permissionsError);
        if (isSessionEndingError(permissionsError)) {
          clearAuth();
        } else {
          setPermissions(null);
        }
      }
    } catch (error) {
      console.error('Error initializing user:', error);
      if (isSessionEndingError(error)) {
        clearAuth();
      } else {
        // Do not render protected content from stale localStorage when the
        // authoritative identity check could not be completed.
        setUser(null);
        setPermissions(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  // Login function
  const login = useCallback((userData, userPermissions = null) => {
    const normalizedUser = userPermissions
      ? {
          ...userData,
          is_admin: userData?.is_admin ?? userPermissions.is_admin,
          is_intrusion: userData?.is_intrusion ?? userPermissions.is_intrusion,
          is_super_admin: userData?.is_super_admin ?? userPermissions.is_super_admin,
          role: userData?.role || userPermissions.user_type,
        }
      : userData;

    setUser(normalizedUser);
    setIsAuthenticated(true);
    if (userPermissions) {
      setPermissions(normalizeAdminPermissions(userPermissions));
    } else {
      // Fetch permissions if not provided with retry logic
      getUserPermissions()
        .then((perms) => setPermissions(normalizeAdminPermissions(perms)))
        .catch(err => {
          console.error('Failed to fetch permissions after login:', err);
          // Retry once after 1 second
          setTimeout(() => {
            getUserPermissions()
              .then((perms) => setPermissions(normalizeAdminPermissions(perms)))
              .catch(retryErr => {
                console.error('Retry failed to fetch permissions:', retryErr);
                // If still fails, clear auth to force re-login
                if (isSessionEndingError(retryErr)) {
                  clearAuth();
                }
              });
          }, 1000);
        });
    }
  }, [clearAuth]);

  // Logout function
  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  // Refresh user data and permissions
  const refreshUser = useCallback(async () => {
    try {
      const [userData, userPerms] = await Promise.all([
        getCurrentUser(),
        getUserPermissions()
      ]);

        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        }

        if (userPerms) {
          setPermissions(normalizeAdminPermissions(userPerms));
          setUser((prevUser) => {
            const baseUser = prevUser || userData || {};
            return {
              ...baseUser,
              is_super_admin: baseUser.is_super_admin ?? userPerms.is_super_admin,
            };
          });
        }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      if (isSessionEndingError(error)) {
        clearAuth();
      }
    }
  }, [clearAuth]);

  // Check if user has admin role
  const isAdmin = useCallback(() => {
    if (isTruthyFlag(user?.is_admin)) return true;
    if (typeof user?.role === 'string' && user.role.toLowerCase() === 'admin') return true;
    return isTruthyFlag(permissions?.is_admin);
  }, [user, permissions]);

  // Check if user has intrusion access
  const isIntrusion = useCallback(() => {
    if (isTruthyFlag(user?.is_intrusion)) return true;
    if (typeof user?.role === 'string' && user.role.toLowerCase() === 'intrusion') return true;
    if (permissions?.user_type === 'intrusion') return true;
    return isTruthyFlag(permissions?.is_intrusion);
  }, [user, permissions]);

  const isSuperAdmin = useCallback(() => {
    if (isTruthyFlag(user?.is_super_admin)) return true;
    return isTruthyFlag(permissions?.is_super_admin);
  }, [user, permissions]);

  // Get user type: 'admin', 'intrusion', or null
  const getUserType = useCallback(() => {
    if (permissions?.user_type === 'admin' || permissions?.user_type === 'intrusion') {
      return permissions.user_type;
    }
    
    const admin = isAdmin();
    const intrusion = isIntrusion();
    
    if (admin) return 'admin';
    if (!admin && intrusion) return 'intrusion';

    if (typeof user?.role === 'string') {
      const normalizedRole = user.role.toLowerCase();
      if (normalizedRole === 'admin' || normalizedRole === 'intrusion') {
        return normalizedRole;
      }
    }

    return null;
  }, [user, permissions, isAdmin, isIntrusion]);

  // Initialize on mount
  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  const value = {
    user,
    permissions,
    loading,
    isAuthenticated,
    isAdmin: isAdmin(),
    isIntrusion: isIntrusion(),
    isSuperAdmin: isSuperAdmin(),
    userType: getUserType(),
    login,
    logout,
    refreshUser,
    clearAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
